import { isInTypeGuard } from "@bcmi-labs/art-toolkit/utils"
import { compareSemVer, isValidSemVer } from "semver-parser"

import { APP_LAB_FQBNS, arduinoDeviceDataMap, deviceImgMap, uniqueDeviceImgMap } from "@/constants/devices"
import type { ThemedImageAssetPath } from "@/types/assets"
import type { ArduinoDevice } from "@/types/devices"

const LORA_DEVICE_TYPE = "lora-device"
const PHONE_DEVICE_TYPE = "phone"
const MANUAL_DEVICE_FQBN = "device:generic:manual"

/*
 * =============================================================================
 * Device identification utils
 * =============================================================================
 */

export function isDeviceLora(device: Pick<ArduinoDevice, "type" | "fqbn">) {
  return device.type === LORA_DEVICE_TYPE || (device.fqbn && /arduino:samd:mkrwan13[01]0$/.test(device.fqbn))
}

export function isDeviceMobile(device: Pick<ArduinoDevice, "type" | "fqbn">) {
  return device.type === PHONE_DEVICE_TYPE && !device.fqbn
}

export function isDeviceManual(device: Pick<ArduinoDevice, "fqbn">) {
  return device.fqbn === MANUAL_DEVICE_FQBN
}

/** RBAC flag guarding the network credentials flow. Devices without a unique hardware ID get theirs from the sketch. */
export function getNetworkConfigPermissionFlag(device: Pick<ArduinoDevice, "unique_hardware_id">) {
  return device.unique_hardware_id ? ("credential::update" as const) : ("sketch::update" as const)
}

export function isDeviceEsp32(fqbn: string | undefined) {
  return fqbn?.startsWith("esp32")
}

/** Whether a board (identified by fqbn) must be provisioned from Arduino App Lab (Q family). */
export function isAppLabFqbn(fqbn: string | undefined): boolean {
  return !!fqbn && APP_LAB_FQBNS.includes(fqbn)
}

export function formatOtaDuration(endDate: string | undefined, startDate: string | undefined) {
  try {
    if (!endDate || !startDate) {
      return "-"
    }

    const end = new Date(endDate).getTime()
    const start = new Date(startDate).getTime()
    const diff = Math.max(0, end - start)

    const minutes = Math.floor(diff / 60000)
    const seconds = Math.floor((diff % 60000) / 1000)

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`
    }
    return `${seconds} Seconds`
  } catch {
    return "-"
  }
}

/*
 * =============================================================================
 * Device utilities
 * =============================================================================
 */

/** Check if the device has an expired CA certificate */
export function isDeviceCAExpired(device: {
  fqbn?: string
  type: string
  issuer_ca?: string
  lib_version?: string
  created_at?: string
}) {
  /*
   * ℹ️ Some devices skip the CA check.
   *
   * - UNOQ/VENTUNOQ etc... use a different IOT library that is not C/C++ based, and doesn't suffer from the CA expiration issue
   *- Portenta X8 running Python.
   */
  if (device.fqbn && (isAppLabFqbn(device.fqbn) || ["arduino:python:portenta_x8"].includes(device.fqbn))) {
    return false
  }

  const creationDate = device.created_at ? new Date(device.created_at).getTime() : new Date().getTime()
  const expirationDate = new Date("2025-01-27").getTime()

  const isCreatedBeforeMigrationDate = creationDate < expirationDate

  // ESP32 devices don't have a CA, but must have a 2.3.0 or higher library version
  if (isDeviceEsp32(device.fqbn)) {
    // If there's no lib_version, we assume it's outdated and warn the user
    return device.lib_version
      ? isValidSemVer(device.lib_version) && compareSemVer(device.lib_version, "2.3.0") === -1
      : isCreatedBeforeMigrationDate
  }

  // Old CA Arduino device need to be migrated
  if (device.issuer_ca === "Arduino") {
    return true
  }

  // If the device is mobile, we don't need to do further checks as it doesn't have a lib_version.
  if (isDeviceMobile(device)) return false

  // New CA Arduino show warning only if library needs to be updated and the board has been connected at least once
  if (device.issuer_ca === "Arduino_v2") {
    // If it was created before Jan 27 2025 and we have no lib_version, we assume it's outdated and warn the user
    return device.lib_version
      ? isValidSemVer(device.lib_version) && compareSemVer(device.lib_version, "2.3.0") === -1
      : isCreatedBeforeMigrationDate
  }

  // If no condition is met, we assume the device is up to date. ESP8266, LoRa, generic...
  return false
}

/**
 * Generate a device image path based on the FQBN.
 *
 * @param fqbn
 * @returns
 */

export function getDeviceImgPath(device: { type: string; fqbn?: string } | undefined): ThemedImageAssetPath {
  // Check if FQBN is present in the unique device images map
  const hasUniqueImage =
    device?.fqbn && isInTypeGuard(device.fqbn, uniqueDeviceImgMap) ? uniqueDeviceImgMap[device.fqbn] : undefined
  if (hasUniqueImage) return hasUniqueImage

  // Check if device is mobile
  if (device && isDeviceMobile(device)) {
    return "images/devices/boards/device-phone.svg"
  }

  // Get family type via FQBN.
  const family = isInTypeGuard(device?.fqbn, arduinoDeviceDataMap)
    ? arduinoDeviceDataMap[device.fqbn].family
    : "unknown" // Fallback to unknown device.

  return deviceImgMap[family]
}
/**
 * Get the device name based on the device type.
 *
 * @param boardsDef
 * @param device
 * @returns
 */
export const getBoardDeviceName = (device: {
  // All the fields are required, but we can pass undefined if they're not available (so we don't forget them since they come from various sources)
  fqbn: string | undefined
  name: string | undefined
  type: string | undefined
}) => {
  if (device.type === LORA_DEVICE_TYPE) return "LoRaWAN device"

  if (isDeviceMobile({ type: device.type || "", fqbn: device.fqbn })) return "Mobile phone"
  if (isDeviceManual(device)) return "Manually configured device"

  return device.name || "-"
}

/*
 * =============================================================================
 * FQBN Filtering utilities
 * =============================================================================
 */

/**
 * Helper function to extract vendor from an FQBN.
 * FQBN format: VENDOR:ARCHITECTURE:BOARD_ID
 *
 * @param fqbn - The Fully Qualified Board Name
 * @returns The vendor part of the FQBN, or undefined if invalid format
 */
export function extractVendorFromFqbn(fqbn: string): string | undefined {
  const parts = fqbn.split(":")
  return parts.length >= 3 ? parts[0] : undefined
}

/**
 * Evaluates which FQBNs from a boards list should be included based on filter criteria.
 *
 * Implements the following logic:
 * - If the filter wants to match VENDOR = arduino, then the matching logic is strict:
 *   all items whose FQBN is not matching are not allowed.
 * - If even one of the filters is not targeting VENDOR = arduino, then we allow
 *   items whose FQBN is unknown.
 *
 * @param filter - Filter configuration (either specific device or list of FQBN patterns)
 * @param boardsList - Array of boards with FQBN properties
 * @returns Array of FQBNs that should be included based on filtering criteria
 */
export function evaluateFqbnFilter(
  filter: (string | RegExp)[] | undefined,
  boardsList: { fqbn: string }[] | undefined
): string[] {
  if (!filter) return []

  // Handle array filter case
  const polishedFilter = {
    regex: filter.filter(f => f instanceof RegExp),
    fqbns: filter.filter(f => typeof f === "string")
  }

  if (!boardsList?.length) return polishedFilter.fqbns

  // Find all FQBNs that match the regex patterns
  const matchingFqbns = boardsList
    .filter(item => polishedFilter.regex.some(re => re.test(item.fqbn)))
    .map(item => item.fqbn)

  const allMatchingFqbns = polishedFilter.fqbns.concat(matchingFqbns)

  // Check if any filter is targeting arduino vendor specifically
  const hasArduinoVendorFilter = allMatchingFqbns.some(fqbn => {
    const vendor = extractVendorFromFqbn(fqbn)
    return vendor === "arduino"
  })

  // If no arduino vendor filter, include all matches (lenient mode)
  if (!hasArduinoVendorFilter) {
    return allMatchingFqbns
  }

  // If arduino vendor filter exists, apply strict filtering:
  // Only include FQBNs that have a known vendor (exclude unknown/malformed FQBNs)
  return allMatchingFqbns.filter(fqbn => {
    const vendor = extractVendorFromFqbn(fqbn)
    return vendor !== undefined
  })
}
